import React, { useState, useEffect } from 'react';
import { useTranslation } from '../localization/useAppTranslation';
import { ThemedSpinner } from './ThemedSpinner';
import '../styles/Loading.css';

interface LoadingScreenProps {
  message?: string;
  submessage?: string;
  isError?: boolean;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({ 
  message,
  submessage,
  isError = false
}) => {
  const { t } = useTranslation('auth');
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [dots, setDots] = useState('');
  const loadingMessages = [
    t('loading.initializing'),
    t('loading.securingConnection'),
    t('loading.validatingCredentials'),
    t('loading.preparingDashboard'),
    t('loading.almostReady'),
  ];

  useEffect(() => {
    // Only show cycling messages if not an error state
    if (isError) {
      return;
    }

    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2000);

    const dotsInterval = setInterval(() => {
      setDots((prev) => {
        if (prev === '...') return '';
        return prev + '.';
      });
    }, 500);

    return () => {
      clearInterval(messageInterval);
      clearInterval(dotsInterval);
    };
  }, [isError]);

  return (
    <div className={`loading-screen ${isError ? 'error-state' : ''}`}>
      <div className="loading-container">
        <div className="loading-animation">
          <div className="loading-spinner">
            {!isError && (
              <ThemedSpinner
                size="lg"
                className="loading-screen-spinner"
                ariaLabel={t('common:status.loading')}
              />
            )}
          </div>
          <div className="loading-logo">
            <div className="logo-icon">
              {isError ? (
                <div className="error-icon">⚠️</div>
              ) : (
                <img src="/avatars/bot-avatar.svg" alt="SpeedGauge Bot" />
              )}
            </div>
          </div>
        </div>
        
        <div className="loading-content">
          <img src="/images/logo.svg" alt="SpeedGauge" className="loading-title" />
          <p className="loading-subtitle">SpeedGauge Coach AI</p>
          
          <div className="loading-message">
            <p className={`primary-message ${isError ? 'error' : ''}`}>
              {message || isError
                ? message || t('common:status.loading')
                : `${loadingMessages[currentMessageIndex]}${dots}`}
            </p>
            {submessage && (
              <p className="sub-message">{submessage}</p>
            )}
          </div>
          
          {!isError && (
            <div className="loading-progress">
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
